/**
 * Money is integer minor units plus an ISO 4217 currency (architecture.md › Quantities and money).
 * Prices are stored without tax, in hundredths of a minor unit (ADR 0009): 12,49 € incl. 19 % is
 * stored as 104 958. Tax rates are basis points: 1900 is 19 %, 810 is 8,1 %.
 */
export const CURRENCIES = [
  "EUR",
  "USD",
  "GBP",
  "CHF",
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "CAD",
  "AUD",
  "JPY",
] as const;

export type CurrencyCode = (typeof CURRENCIES)[number];

const digitsCache = new Map<string, number>();

export function currencyDigits(currency: string): number {
  let digits = digitsCache.get(currency);
  if (digits === undefined) {
    digits =
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
    digitsCache.set(currency, digits);
  }
  return digits;
}

export function toMinor(amount: number, currency: string): number {
  return Math.round(amount * 10 ** currencyDigits(currency));
}

export function fromMinor(amountMinor: number, currency: string): number {
  return amountMinor / 10 ** currencyDigits(currency);
}

/** Stored prices are in hundredths of a minor unit, without tax. */
export const EXCL_TAX_PER_MINOR = 100;

const BP = 10_000;

/** A stored amount (excl. tax) in minor units, with `rateBp` tax added; 0 shows it excl. tax. */
export function shownPrice(exclTax: number, rateBp: number): number;
export function shownPrice(exclTax: number | null, rateBp: number): number | null;
export function shownPrice(exclTax: number | null, rateBp: number): number | null {
  if (exclTax === null) return null;
  return Math.round((exclTax * (BP + rateBp)) / (BP * EXCL_TAX_PER_MINOR));
}

/**
 * The amount to store for a price entered in minor units with `rateBp` tax included. A price that
 * still shows as entered keeps its stored amount (`current`), so saving it again after a rate
 * change doesn't change it.
 */
export function storedPrice(shownMinor: number, rateBp: number, current?: number | null): number;
export function storedPrice(
  shownMinor: number | null,
  rateBp: number,
  current?: number | null,
): number | null;
export function storedPrice(
  shownMinor: number | null,
  rateBp: number,
  current?: number | null,
): number | null {
  if (shownMinor === null) return null;
  if (current !== undefined && current !== null && shownPrice(current, rateBp) === shownMinor) {
    return current;
  }
  return Math.round((shownMinor * EXCL_TAX_PER_MINOR * BP) / (BP + rateBp));
}
